// Statement number allocation, as arithmetic. Pure — no Medplum and no FHIR, so it can only decide
// which number the bot *proposes*; whether that proposal survives is `create-statements.ts`.
//
// Numbers are parsed to integers, never compared as strings: "10000" < "9999" lexically, so a
// practice's 10,000th statement would otherwise reset the sequence. `padStart` widens past four
// digits on its own, so nothing breaks at the boundary.

/** A primary statement number: `0001`. */
const PRIMARY_PATTERN = /^(\d+)$/;

/** A sub-statement number: `0001-2`. The suffix itself is the parent link. */
const DERIVED_PATTERN = /^(\d+)-(\d+)$/;

const STATEMENT_NUMBER_WIDTH = 4;

export function padStatementNumber(value: number): string {
  return String(value).padStart(STATEMENT_NUMBER_WIDTH, "0");
}

/**
 * The highest primary number in use. Sub-statements are ignored entirely — `0002-1` does not consume
 * `0003`, so the sequence advances only on primaries. Values matching neither pattern (a legacy or
 * hand-imported number) are skipped, so they can never crash allocation.
 *
 * Cancelled statements are deliberately counted: their numbers stay consumed. Excluding them would
 * make the allocator propose a number that is already on disk, which `ifNoneExist` then matches,
 * burning a retry per cancelled statement at the tail of the sequence.
 */
export function highestPrimary(existing: string[]): number {
  return existing.reduce((highest, value) => {
    const match = PRIMARY_PATTERN.exec(value);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
}

/** A contiguous block of `count` primary numbers, for a multi-Service selection. */
export function nextPrimaryBlock(existing: string[], count: number): string[] {
  const start = highestPrimary(existing) + 1;
  return Array.from({ length: count }, (_, index) => padStatementNumber(start + index));
}

/** The next `-N` under one parent: highest suffix in the family + 1. */
export function nextDerived(parent: string, family: string[]): string {
  const normalized = normalizeParentNumber(parent);
  if (!normalized) {
    throw new Error(`Not a primary statement number: ${parent}`);
  }

  const highestSuffix = family.reduce((highest, value) => {
    const match = DERIVED_PATTERN.exec(value);
    if (!match || padStatementNumber(Number(match[1])) !== normalized) return highest;
    return Math.max(highest, Number(match[2]));
  }, 0);

  return `${normalized}-${highestSuffix + 1}`;
}

/**
 * Where a number sits in its family's billing order: `0` for the primary, `N` for `{parent}-N`.
 *
 * A family bills one set of charges to successive payers in suffix order, so comparing suffixes
 * orders the hops. A number matching neither pattern sorts as a primary, which keeps a legacy or
 * hand-imported number from being read as the family's latest hop.
 */
export function derivedSuffix(value: string): number {
  const match = DERIVED_PATTERN.exec(value.trim());
  return match ? Number(match[2]) : 0;
}

/** `"2"` and `"0002"` name the same parent; returns the canonical form, or undefined if not primary. */
export function normalizeParentNumber(parent: string): string | undefined {
  const match = PRIMARY_PATTERN.exec(parent.trim());
  return match ? padStatementNumber(Number(match[1])) : undefined;
}

/**
 * The padded form of any statement number: `"2"` becomes `"0002"` and `"2-1"` becomes `"0002-1"`.
 *
 * @param value A primary or derived statement number, possibly unpadded or with spaces around it.
 * @returns The padded number, or `undefined` when the value is neither a primary nor a derived number.
 */
export function normalizeStatementNumber(value: string): string | undefined {
  const trimmed = value.trim();
  const derived = DERIVED_PATTERN.exec(trimmed);
  if (derived) return `${padStatementNumber(Number(derived[1]))}-${Number(derived[2])}`;
  return normalizeParentNumber(trimmed);
}

/**
 * The primary at the root of a number's family: `0002-3` and `0002` both belong to `0002`.
 *
 * Unlike `predecessorNumber`, this walks all the way to the primary rather than one hop, which is
 * what groups a whole family together. The root is always padded, so an unpadded primary `37` and
 * its sub-statement `0037-1` share the family `0037`. A number matching neither pattern is its own
 * root, so a legacy or hand-imported number forms a family of one instead of being folded into
 * another.
 *
 * @param value A statement number.
 * @returns The family's primary number.
 */
export function familyPrimaryNumber(value: string): string {
  const trimmed = value.trim();
  const match = DERIVED_PATTERN.exec(trimmed);
  if (match) return padStatementNumber(Number(match[1]));
  return normalizeParentNumber(trimmed) ?? trimmed;
}

/**
 * The number of the statement a sub-statement was billed forward from.
 *
 * One hop back, not all the way to the primary: `0002-1` came from `0002`, and `0002-2` came from
 * `0002-1`, because each hop re-bills what the hop before it left outstanding.
 *
 * @param value A statement number.
 * @returns The predecessor's number, or `undefined` for a primary — it has no predecessor.
 */
export function predecessorNumber(value: string): string | undefined {
  const match = DERIVED_PATTERN.exec(value.trim());
  if (!match) return undefined;

  const parent = padStatementNumber(Number(match[1]));
  const suffix = Number(match[2]);

  return suffix > 1 ? `${parent}-${suffix - 1}` : parent;
}
